import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { 
  Ticket, 
  CheckCircle, 
  AlertTriangle,
  Loader2,
  ArrowRight,
  Search,
  Paperclip,
  X,
  FileText,
  Image,
  Clock,
  MessageSquare,
  Upload
} from 'lucide-react';
import axios from 'axios';

// Direct API URL for public endpoints (no auth needed)
const getApiBaseUrl = () => {
  return import.meta.env.VITE_API_URL || '/api';
};

const publicApi = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

interface TicketCategory {
  id: string;
  name: string;
  description?: string;
  icon?: string;
}

interface EmployeeInfo {
  id: string;
  employeeId: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  jobTitle: string | null;
  department: string | null;
  departmentId: string | null;
  branch: string | null;
  branchId: string | null;
  organizationId: string;
}

interface OrgBranding {
  name: string;
  code: string;
  logo: string | null;
  systemName: string;
}

interface TicketStatus {
  ticketNumber: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
  closedAt: string | null;
  category: { name: string; icon?: string } | null;
  assignee: { firstName: string; lastName: string } | null;
  attachments: Array<{
    id: string;
    originalName: string;
    mimeType: string;
    size: number;
    path: string;
  }>;
  comments: Array<{
    id: string;
    content: string;
    createdAt: string;
    author: { firstName: string; lastName: string } | null;
  }>;
  history: Array<{
    action: string;
    oldValue: string | null;
    newValue: string | null;
    createdAt: string;
  }>;
}

type Step = 'verify' | 'form' | 'success' | 'status';

const PRIORITIES = [
  { value: 'LOW', label: 'Low', description: 'Can wait a few days' },
  { value: 'MEDIUM', label: 'Medium', description: 'Normal priority' },
  { value: 'HIGH', label: 'High', description: 'Affecting my work' },
  { value: 'URGENT', label: 'Urgent', description: 'Work stopped' },
];

export default function PublicSubmitTicketPage() {
  // State
  const [step, setStep] = useState<Step>('verify');
  const [employeeIdInput, setEmployeeIdInput] = useState('');
  const [employeeInfo, setEmployeeInfo] = useState<EmployeeInfo | null>(null);
  const [error, setError] = useState('');
  const [ticketNumber, setTicketNumber] = useState('');
  const [ticketNumberSearch, setTicketNumberSearch] = useState('');
  const [ticketStatus, setTicketStatus] = useState<TicketStatus | null>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [duplicateWarning, setDuplicateWarning] = useState<any>(null);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'MEDIUM',
    categoryId: '',
  });

  // Fetch organization branding
  const { data: brandingData } = useQuery({
    queryKey: ['public-branding'],
    queryFn: async () => {
      const response = await publicApi.get('/tickets/public/branding');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const branding: OrgBranding = brandingData?.data || { 
    name: 'IT Support', 
    code: '', 
    logo: null, 
    systemName: 'Glide' 
  };

  // Fetch ticket categories
  const { data: categoriesData, isLoading: categoriesLoading } = useQuery({
    queryKey: ['public-ticket-categories'],
    queryFn: async () => {
      const response = await publicApi.get('/tickets/public/categories');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const ticketCategories: TicketCategory[] = categoriesData?.data || [];

  // Check for stored employee info on mount
  useEffect(() => {
    const stored = localStorage.getItem('ticket_employee');
    if (stored) {
      try {
        const emp = JSON.parse(stored) as EmployeeInfo;
        setEmployeeInfo(emp);
        setStep('form');
      } catch {
        localStorage.removeItem('ticket_employee');
      }
    }
  }, []);

  // Verify employee mutation
  const verifyEmployeeMutation = useMutation({
    mutationFn: async (empId: string) => {
      const response = await publicApi.get(`/tickets/public/verify-employee/${empId}`);
      return response.data;
    },
    onSuccess: (data) => {
      const emp = data.data as EmployeeInfo;
      setEmployeeInfo(emp);
      localStorage.setItem('ticket_employee', JSON.stringify(emp));
      setStep('form');
      setError('');
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Employee not found. Please check your ID and try again.');
    },
  });

  // Submit ticket mutation with file uploads
  const submitTicketMutation = useMutation({
    mutationFn: async (data: typeof formData & { employeeId: string }) => {
      const formDataObj = new FormData();
      formDataObj.append('employeeId', data.employeeId);
      formDataObj.append('title', data.title);
      formDataObj.append('description', data.description);
      formDataObj.append('categoryId', data.categoryId);
      formDataObj.append('priority', data.priority);
      
      // Add attachments
      attachments.forEach((file) => {
        formDataObj.append('attachments', file);
      });

      const response = await axios.post(`${getApiBaseUrl()}/tickets/public`, formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000, // 60s for file uploads
      });
      return response.data;
    },
    onSuccess: (data) => {
      setTicketNumber(data.data.ticketNumber);
      setStep('success');
      setFormData({ title: '', description: '', priority: 'MEDIUM', categoryId: '' });
      setAttachments([]);
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to submit ticket. Please try again.');
    },
  });

  // Check ticket status mutation
  const checkStatusMutation = useMutation({
    mutationFn: async (ticketNum: string) => {
      const response = await publicApi.get(`/tickets/public/status/${ticketNum}`);
      return response.data;
    },
    onSuccess: (data) => {
      setTicketStatus(data.data);
      setStep('status');
      setError('');
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Ticket not found. Please check the ticket number.');
      setTicketStatus(null);
    },
  });

  // Check for duplicate tickets mutation
  const checkDuplicatesMutation = useMutation({
    mutationFn: async ({ employeeId, title }: { employeeId: string; title: string }) => {
      const response = await publicApi.post('/tickets/public/check-duplicates', { employeeId, title });
      return response.data;
    },
  });

  const handleVerifyEmployee = (e: React.FormEvent) => {
    e.preventDefault();
    if (!employeeIdInput.trim()) {
      setError('Please enter your Employee ID');
      return;
    }
    setError('');
    verifyEmployeeMutation.mutate(employeeIdInput.trim());
  };

  const handleCheckStatus = (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticketNumberSearch.trim()) {
      setError('Please enter a ticket number');
      return;
    }
    setError('');
    checkStatusMutation.mutate(ticketNumberSearch.trim().toUpperCase());
  };

  const handleSwitchEmployee = () => {
    localStorage.removeItem('ticket_employee');
    setEmployeeInfo(null);
    setEmployeeIdInput('');
    setStep('verify');
    setError('');
  };

  // Actually submit the ticket (after duplicate check)
  const doSubmitTicket = () => {
    if (!employeeInfo) return;
    setShowDuplicateWarning(false);
    setDuplicateWarning(null);
    submitTicketMutation.mutate({
      ...formData,
      employeeId: employeeInfo.employeeId,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.description || !formData.categoryId) {
      setError('Please fill in all required fields');
      return;
    }
    if (!employeeInfo) return;
    
    setError('');
    
    // Check for duplicates first
    try {
      const result = await checkDuplicatesMutation.mutateAsync({
        employeeId: employeeInfo.employeeId,
        title: formData.title,
      });
      
      if (result.data?.hasPotentialDuplicates && result.data.potentialDuplicates.length > 0) {
        setDuplicateWarning(result.data);
        setShowDuplicateWarning(true);
        return;
      }
    } catch {
      // If duplicate check fails, proceed with submission
    }
    
    doSubmitTicket();
  };

  const handleNewTicket = () => {
    setStep('form');
    setTicketNumber('');
    setError('');
    setAttachments([]);
    setDuplicateWarning(null);
    setShowDuplicateWarning(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const maxFiles = 5;
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        setError(`File "${file.name}" is too large (max 10MB)`);
        return false;
      }
      return true;
    }).slice(0, maxFiles - attachments.length);
    
    setAttachments(prev => [...prev, ...validFiles].slice(0, maxFiles));
    e.target.value = ''; // Reset input
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'OPEN': return 'bg-blue-100 text-blue-800';
      case 'IN_PROGRESS': return 'bg-yellow-100 text-yellow-800';
      case 'PENDING': return 'bg-orange-100 text-orange-800';
      case 'RESOLVED': return 'bg-green-100 text-green-800';
      case 'CLOSED': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Header component
  const Header = () => {
    // Build full logo URL if it's a relative path
    const logoUrl = branding.logo 
      ? (branding.logo.startsWith('http') ? branding.logo : `${getApiBaseUrl().replace('/api', '')}${branding.logo}`)
      : null;
    
    return (
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center gap-3">
          {logoUrl ? (
            <img 
              src={logoUrl} 
              alt={branding.name}
              className="h-10 object-contain"
            />
          ) : (
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <Ticket className="w-5 h-5 text-white" />
            </div>
          )}
          <h1 className="text-lg font-semibold text-slate-900 dark:text-white">
            {branding.name || branding.systemName || 'IT Support'}
          </h1>
        </div>
      </header>
    );
  };

  // STEP 1: Employee Verification
  if (step === 'verify') {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center p-5">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 max-w-md w-full p-8">
            <div className="text-center mb-7">
              <div className="w-14 h-14 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <Ticket className="w-7 h-7 text-slate-600 dark:text-slate-300" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-1">
                Submit a Support Request
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Enter your Employee ID to get started
              </p>
            </div>

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-5 flex items-center gap-2 text-red-700 dark:text-red-300 text-sm">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <form onSubmit={handleVerifyEmployee}>
              <div className="mb-5">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                  Employee ID *
                </label>
                <input
                  type="text"
                  value={employeeIdInput}
                  onChange={(e) => { setEmployeeIdInput(e.target.value); setError(''); }}
                  placeholder="e.g., EMP001"
                  autoFocus
                  className="w-full h-11 px-4 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <button
                type="submit"
                disabled={verifyEmployeeMutation.isPending}
                className="w-full h-11 bg-primary hover:bg-primary/90 disabled:bg-slate-400 text-white font-medium rounded-lg flex items-center justify-center gap-2 transition-colors"
              >
                {verifyEmployeeMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg border border-slate-200 dark:border-slate-600">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2 mb-1">
                <span>💡</span>
                First time here?
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 pl-6">
                Your Employee ID can be found on your staff badge, pay slip, or HR documents.
              </p>
            </div>

            {/* Check Status Section */}
            <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                <Search className="w-4 h-4" />
                Already submitted a ticket?
              </p>
              <form onSubmit={handleCheckStatus} className="flex gap-2">
                <input
                  type="text"
                  value={ticketNumberSearch}
                  onChange={(e) => { setTicketNumberSearch(e.target.value.toUpperCase()); setError(''); }}
                  placeholder="TKT-2025-00001"
                  className="flex-1 h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm"
                />
                <button
                  type="submit"
                  disabled={checkStatusMutation.isPending}
                  className="h-10 px-4 bg-slate-600 hover:bg-slate-700 disabled:bg-slate-400 text-white font-medium rounded-lg flex items-center justify-center gap-1 transition-colors text-sm"
                >
                  {checkStatusMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Search className="w-4 h-4" />
                      Check
                    </>
                  )}
                </button>
              </form>
            </div>

            <p className="text-center mt-5 text-xs text-slate-400">
              Powered by <span className="font-semibold">Glide</span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // STEP 3: Success
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center p-5">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 max-w-md w-full p-8 text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>

            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-1">
              Ticket Submitted Successfully
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">
              Your support request has been received
            </p>

            <div className="bg-slate-100 dark:bg-slate-700 rounded-lg p-5 mb-6">
              <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide font-medium mb-1">
                Ticket Number
              </p>
              <p className="text-2xl font-bold text-primary font-mono">
                {ticketNumber}
              </p>
            </div>

            <div className="text-left bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 mb-6">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                What happens next?
              </p>
              <ul className="text-sm text-slate-500 dark:text-slate-400 space-y-1.5 list-disc pl-4">
                <li>Our IT team will review your request</li>
                <li>You'll receive updates via email</li>
                <li>Save your ticket number for reference</li>
              </ul>
            </div>

            <button
              onClick={handleNewTicket}
              className="w-full h-11 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg transition-colors"
            >
              Submit Another Ticket
            </button>

            <button
              onClick={() => {
                setTicketNumberSearch(ticketNumber);
                checkStatusMutation.mutate(ticketNumber);
              }}
              className="w-full mt-3 h-10 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 font-medium rounded-lg transition-colors text-sm"
            >
              View Ticket Status →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // STEP 4: Status View
  if (step === 'status' && ticketStatus) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col">
        <Header />
        <div className="flex-1 flex items-start justify-center p-5 overflow-y-auto">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 max-w-2xl w-full p-8 my-5">
            {/* Back button */}
            <button
              onClick={() => { setStep('verify'); setTicketStatus(null); setTicketNumberSearch(''); }}
              className="text-sm text-primary hover:underline mb-4 flex items-center gap-1"
            >
              ← Back to home
            </button>

            {/* Ticket header */}
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400 font-mono mb-1">
                  {ticketStatus.ticketNumber}
                </p>
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                  {ticketStatus.title}
                </h2>
                {ticketStatus.category && (
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    {ticketStatus.category.icon} {ticketStatus.category.name}
                  </p>
                )}
              </div>
              <div className="flex flex-col items-end gap-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(ticketStatus.status)}`}>
                  {ticketStatus.status.replace(/_/g, ' ')}
                </span>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {ticketStatus.priority} Priority
                </span>
              </div>
            </div>

            {/* Description */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Description</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-wrap bg-slate-50 dark:bg-slate-700/50 p-4 rounded-lg">
                {ticketStatus.description}
              </p>
            </div>

            {/* Attachments */}
            {ticketStatus.attachments && ticketStatus.attachments.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />
                  Attachments ({ticketStatus.attachments.length})
                </h3>
                <div className="space-y-2">
                  {ticketStatus.attachments.map((att) => (
                    <a
                      key={att.id}
                      href={`${getApiBaseUrl().replace('/api', '')}${att.path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-700/50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      {att.mimeType?.startsWith('image/') ? (
                        <Image className="w-4 h-4 text-blue-500" />
                      ) : (
                        <FileText className="w-4 h-4 text-slate-500" />
                      )}
                      <span className="text-sm text-primary hover:underline truncate">
                        {att.originalName}
                      </span>
                      <span className="text-xs text-slate-400">
                        ({formatFileSize(att.size)})
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Timeline info */}
            <div className="mb-6 grid grid-cols-2 gap-4">
              <div className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Created</p>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatDate(ticketStatus.createdAt)}
                </p>
              </div>
              {ticketStatus.resolvedAt && (
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <p className="text-xs text-green-600 dark:text-green-400 mb-1">Resolved</p>
                  <p className="text-sm font-medium text-green-700 dark:text-green-300 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    {formatDate(ticketStatus.resolvedAt)}
                  </p>
                </div>
              )}
              {ticketStatus.assignee && (
                <div className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Assigned To</p>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {ticketStatus.assignee.firstName} {ticketStatus.assignee.lastName}
                  </p>
                </div>
              )}
            </div>

            {/* Comments */}
            {ticketStatus.comments && ticketStatus.comments.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Updates ({ticketStatus.comments.length})
                </h3>
                <div className="space-y-3">
                  {ticketStatus.comments.map((comment) => (
                    <div key={comment.id} className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-l-4 border-blue-500">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                          {comment.author ? `${comment.author.firstName} ${comment.author.lastName}` : 'IT Support'}
                        </span>
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          {formatDate(comment.createdAt)}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700 dark:text-slate-300">
                        {comment.content}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Status explanation */}
            <div className="p-4 bg-slate-100 dark:bg-slate-700 rounded-lg">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {ticketStatus.status === 'OPEN' && 'Your ticket is open and awaiting assignment to a support technician.'}
                {ticketStatus.status === 'IN_PROGRESS' && 'A support technician is actively working on your ticket.'}
                {ticketStatus.status === 'PENDING' && 'Your ticket is pending additional information or action.'}
                {ticketStatus.status === 'RESOLVED' && 'Your ticket has been resolved. If you still have issues, please submit a new ticket.'}
                {ticketStatus.status === 'CLOSED' && 'This ticket has been closed. If you need further assistance, please submit a new ticket.'}
              </p>
            </div>

            {/* Submit new ticket button */}
            <button
              onClick={() => { setStep('verify'); setTicketStatus(null); setTicketNumberSearch(''); }}
              className="w-full mt-6 h-11 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg transition-colors"
            >
              Submit New Ticket
            </button>
          </div>
        </div>
      </div>
    );
  }

  // STEP 2: Ticket Form
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col">
      <Header />
      <div className="flex-1 flex items-center justify-center p-3 overflow-y-auto">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 max-w-lg w-full p-5">
          {/* Employee info banner */}
          <div className="flex items-center justify-between p-2 bg-slate-100 dark:bg-slate-700 rounded-lg mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white text-sm font-semibold">
                {employeeInfo?.firstName?.charAt(0) || 'U'}
              </div>
              <div>
                <p className="text-sm font-medium text-slate-900 dark:text-white">
                  {employeeInfo?.name}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {employeeInfo?.department || 'Employee'} • {employeeInfo?.employeeId}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleSwitchEmployee}
              className="text-xs text-primary hover:underline font-medium"
            >
              Switch
            </button>
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-2 mb-3 flex items-center gap-2 text-red-700 dark:text-red-300 text-sm">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Category and Priority in row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Category *
                </label>
                <select
                  value={formData.categoryId}
                  onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                  required
                  disabled={categoriesLoading}
                  className="w-full h-9 px-3 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                <option value="">Select a category...</option>
                {ticketCategories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.icon ? `${cat.icon} ` : ''}{cat.name}
                  </option>
                ))}
              </select>
            </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="w-full h-9 px-3 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  {PRIORITIES.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                Subject *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Brief summary of your issue"
                required
                maxLength={200}
                className="w-full h-9 px-3 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe your issue in detail..."
                required
                rows={3}
                className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              />
            </div>

            {/* Attachments - compact */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1">
                  <Paperclip className="w-3 h-3" />
                  Attachments
                </label>
                <span className="text-xs text-slate-400">Optional • Max 5 files</span>
              </div>
              
              <div 
                className="border border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-3 text-center hover:border-primary transition-colors cursor-pointer"
                onClick={() => document.getElementById('file-input')?.click()}
              >
                <input
                  id="file-input"
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,video/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <Upload className="w-5 h-5 mx-auto text-slate-400" />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Click to upload files
                </p>
              </div>

              {/* Attachment list */}
              {attachments.length > 0 && (
                <div className="mt-2 space-y-1">
                  {attachments.map((file, index) => (
                    <div 
                      key={index} 
                      className="flex items-center justify-between p-1.5 bg-slate-50 dark:bg-slate-700/50 rounded border border-slate-200 dark:border-slate-600"
                    >
                      <div className="flex items-center gap-1.5 min-w-0">
                        {file.type.startsWith('image/') ? (
                          <Image className="w-3 h-3 text-blue-500 flex-shrink-0" />
                        ) : (
                          <FileText className="w-3 h-3 text-slate-500 flex-shrink-0" />
                        )}
                        <span className="text-xs text-slate-700 dark:text-slate-300 truncate">
                          {file.name}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        className="p-0.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={submitTicketMutation.isPending}
              className="w-full h-10 bg-primary hover:bg-primary/90 disabled:bg-slate-400 text-white font-medium rounded-lg flex items-center justify-center gap-2 transition-colors"
            >
              {submitTicketMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Ticket'
              )}
            </button>
          </form>

          {/* Duplicate Warning Modal */}
          {showDuplicateWarning && duplicateWarning && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg max-w-md w-full p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-full">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                    Similar Ticket Found
                  </h3>
                </div>
                
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                  You may have already submitted a ticket for this issue. Would you like to check the status of your existing ticket instead?
                </p>

                <div className="space-y-2 mb-4 max-h-40 overflow-y-auto">
                  {duplicateWarning.potentialDuplicates.map((ticket: any) => (
                    <div 
                      key={ticket.id}
                      className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg border border-slate-200 dark:border-slate-600"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-mono text-primary">{ticket.ticketNumber}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          ticket.status === 'OPEN' ? 'bg-blue-100 text-blue-800' :
                          ticket.status === 'IN_PROGRESS' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {ticket.status.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-200 line-clamp-1">
                        {ticket.title}
                      </p>
                      <button
                        onClick={() => {
                          setTicketNumberSearch(ticket.ticketNumber);
                          checkStatusMutation.mutate(ticket.ticketNumber);
                          setShowDuplicateWarning(false);
                        }}
                        className="text-xs text-primary hover:underline mt-1"
                      >
                        Check status →
                      </button>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => setShowDuplicateWarning(false)}
                    className="flex-1 h-10 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 font-medium rounded-lg transition-colors text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={doSubmitTicket}
                    className="flex-1 h-10 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg transition-colors text-sm"
                  >
                    Submit Anyway
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
